import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Stage, Layer, Rect, Transformer } from "react-konva";
import LayerNode from "./LayerNode.jsx";
import { PALETTE_DRAG_MIME } from "./Palette.jsx";

// Imperative handle exposed via ref:
//   - exportStage() → dataURL cropped exactly to the canvas rect
//     (canvasSize.w × canvasSize.h pixels); anything outside the frame
//     is excluded.
//   - exportNodes(layerIds, {pixelRatio?, padding?}) → dataURL of the
//     bounding box covering the given layers in world coords.
//
// Coordinate system: world coords are decoupled from screen coords by
// the Stage's scale/position. Layers, the canvas frame, and drop coords
// all live in world coords. The Stage is auto-fit so the canvas rect
// fills the editor area, then the user can zoom around the cursor with
// the scroll wheel.

const FIT_PADDING = 16;
const ZOOM_MIN = 0.05;
const ZOOM_MAX = 50;
const ZOOM_STEP = 1.08;

const Canvas = forwardRef(function Canvas(
  {
    layers,
    selectedIds,
    canvasSize,
    onSelectLayers,
    onToggleSelection,
    onTransformLayer,
    onTransformLayers,
    onDuplicateLayersInPlace,
    onAddLayerFromTile,
    onLayerContextMenu,
  },
  ref,
) {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const frameRef = useRef(null);
  const layerNodesRef = useRef(new Map()); // layerId → Konva.Group
  const [size, setSize] = useState({ w: 800, h: 600 });
  // Stage transform: scale uniform, position is top-left of world (0,0)
  // in container pixels. Updated by the auto-fit effect and the wheel
  // handler.
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  // True after the user has interactively zoomed/panned. Container
  // resizes won't auto-refit while this is set so we don't yank the
  // user's view out from under them. Resets on canvas-size change.
  const userMovedViewRef = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const { width, height } = e.contentRect;
        if (width > 0 && height > 0) setSize({ w: Math.floor(width), h: Math.floor(height) });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fitToRect = useCallback(() => {
    const cw = canvasSize?.w || 1;
    const ch = canvasSize?.h || 1;
    const availW = Math.max(1, size.w - FIT_PADDING * 2);
    const availH = Math.max(1, size.h - FIT_PADDING * 2);
    const scale = Math.min(availW / cw, availH / ch);
    const x = (size.w - cw * scale) / 2;
    const y = (size.h - ch * scale) / 2;
    setView({ scale, x, y });
  }, [canvasSize?.w, canvasSize?.h, size.w, size.h]);

  // Canvas-size changes always reset the user's manual zoom and refit.
  useEffect(() => {
    userMovedViewRef.current = false;
  }, [canvasSize?.w, canvasSize?.h]);

  // Refit on first valid container size, on canvas-size change, or on
  // container resize as long as the user hasn't manually zoomed.
  useEffect(() => {
    if (size.w === 0 || size.h === 0) return;
    if (userMovedViewRef.current) return;
    fitToRect();
  }, [size.w, size.h, canvasSize?.w, canvasSize?.h, fitToRect]);

  // Keep the Transformer attached to whatever is currently selected,
  // minus any layer that is locked or hidden — there's no point in
  // showing handles on something the user can't transform.
  useEffect(() => {
    const tr = transformerRef.current;
    if (!tr) return;
    const nodes = (selectedIds || [])
      .map((id) => {
        const layer = layers.find((l) => l.id === id);
        if (!layer || layer.locked || layer.hidden) return null;
        return layerNodesRef.current.get(id);
      })
      .filter(Boolean);
    tr.nodes(nodes);
    tr.getLayer()?.batchDraw();
  }, [selectedIds, layers]);

  const registerNode = useCallback(
    (id, node) => {
      if (node) layerNodesRef.current.set(id, node);
      else layerNodesRef.current.delete(id);
      // Re-attach if this is one of the selected nodes.
      if (selectedIds && selectedIds.includes(id)) {
        const tr = transformerRef.current;
        if (tr) {
          const nodes = selectedIds
            .map((x) => layerNodesRef.current.get(x))
            .filter(Boolean);
          tr.nodes(nodes);
          tr.getLayer()?.batchDraw();
        }
      }
    },
    [selectedIds],
  );

  const handleStageClick = (e) => {
    if (e.target === e.target.getStage()) onSelectLayers([]);
  };

  // Multi-drag: at dragstart we snapshot every selected node's position
  // into a Map. On each dragmove we recompute the dragging node's delta
  // from its OWN dragstart and apply that absolute delta to every
  // sibling's dragstart position. This avoids the per-frame error
  // accumulation that makes deltas drift / oscillate.
  const dragStartPositionsRef = useRef(new Map());

  const handleMultiDragStart = useCallback((draggingId) => {
    if (!selectedIds || selectedIds.length <= 1) {
      dragStartPositionsRef.current.clear();
      return;
    }
    if (!selectedIds.includes(draggingId)) {
      dragStartPositionsRef.current.clear();
      return;
    }
    const positions = new Map();
    for (const id of selectedIds) {
      const node = layerNodesRef.current.get(id);
      if (node) positions.set(id, { x: node.x(), y: node.y() });
    }
    dragStartPositionsRef.current = positions;
  }, [selectedIds]);

  const handleMultiDragMove = useCallback((draggingId, dragX, dragY) => {
    const startMap = dragStartPositionsRef.current;
    const dragStart = startMap.get(draggingId);
    if (!dragStart) return;
    const dx = dragX - dragStart.x;
    const dy = dragY - dragStart.y;
    for (const [id, start] of startMap) {
      if (id === draggingId) continue;
      const node = layerNodesRef.current.get(id);
      if (node) {
        node.x(start.x + dx);
        node.y(start.y + dy);
      }
    }
    stageRef.current?.batchDraw();
  }, []);

  // Alt+drag clones the dragged layer (or the whole multi-selection) at
  // its current transform so the user is effectively dragging a fresh
  // copy. The duplicates sit one z-step below the original; the original
  // stays selected and continues the drag, so on dragend the original is
  // at the new position and the duplicate stays at the source.
  const handleAltDuplicate = useCallback((draggingId) => {
    if (!onDuplicateLayersInPlace) return;
    const ids = (selectedIds && selectedIds.includes(draggingId) && selectedIds.length > 1)
      ? selectedIds
      : [draggingId];
    onDuplicateLayersInPlace(ids);
  }, [selectedIds, onDuplicateLayersInPlace]);

  const handleMultiDragEnd = useCallback(() => {
    const startMap = dragStartPositionsRef.current;
    if (startMap.size === 0) return;
    const patches = {};
    for (const [id] of startMap) {
      const node = layerNodesRef.current.get(id);
      if (node) patches[id] = { x: node.x(), y: node.y() };
    }
    dragStartPositionsRef.current = new Map();
    if (Object.keys(patches).length) onTransformLayers(patches);
  }, [onTransformLayers]);

  const handleSelectFromCanvas = useCallback(
    (layerId, e) => {
      const evt = e.evt || {};
      // Cmd/Ctrl OR Shift toggles the layer in/out of the selection.
      if (evt.metaKey || evt.ctrlKey || evt.shiftKey) {
        onToggleSelection(layerId);
      } else if (selectedIds && selectedIds.length > 1 && selectedIds.includes(layerId)) {
        // Click a member of an existing multi-selection: keep the multi-
        // selection so dragging still moves all of them together.
      } else {
        onSelectLayers([layerId]);
      }
    },
    [selectedIds, onSelectLayers, onToggleSelection],
  );

  // Wheel zoom around cursor. Plain wheel = zoom in/out; magnitude is a
  // small per-tick multiplier so trackpads don't fly off.
  const handleWheel = useCallback((e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const oldScale = view.scale;
    const direction = e.evt.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP;
    const newScale = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, oldScale * direction));
    if (newScale === oldScale) return;
    // World point under cursor stays under cursor: solve for new (x, y)
    // such that (pointer - newPos) / newScale === (pointer - oldPos) / oldScale.
    const worldX = (pointer.x - view.x) / oldScale;
    const worldY = (pointer.y - view.y) / oldScale;
    const newX = pointer.x - worldX * newScale;
    const newY = pointer.y - worldY * newScale;
    userMovedViewRef.current = true;
    setView({ scale: newScale, x: newX, y: newY });
  }, [view.scale, view.x, view.y]);

  // Drag-from-palette to canvas. Drop coords are converted from screen
  // space to world space so the layer lands where the cursor is even
  // when zoomed.
  const handleDragOver = (e) => {
    if (e.dataTransfer.types.includes(PALETTE_DRAG_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    }
  };
  const handleDrop = (e) => {
    const raw = e.dataTransfer.getData(PALETTE_DRAG_MIME);
    if (!raw) return;
    e.preventDefault();
    let payload;
    try { payload = JSON.parse(raw); } catch { return; }
    const stage = stageRef.current;
    if (!stage) return;
    const rect = containerRef.current.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const worldX = (screenX - view.x) / view.scale;
    const worldY = (screenY - view.y) / view.scale;
    onAddLayerFromTile(payload, { x: worldX, y: worldY });
  };

  // Transformer + frame both live in the same Konva Layer and would
  // render into toDataURL output. Hide them for the duration of any
  // export call so the PNG is just the layer pixels.
  const withChromeHidden = (fn) => {
    const tr = transformerRef.current;
    const fr = frameRef.current;
    const layer = tr?.getLayer() || fr?.getLayer();
    const wasTrVisible = tr ? tr.visible() : true;
    const wasFrVisible = fr ? fr.visible() : true;
    if (tr) tr.visible(false);
    if (fr) fr.visible(false);
    layer?.batchDraw();
    try { return fn(); }
    finally {
      if (tr) tr.visible(wasTrVisible);
      if (fr) fr.visible(wasFrVisible);
      layer?.batchDraw();
    }
  };

  // Snapshot the stage transform, reset to identity, run the export, and
  // restore. Lets toDataURL receive (x, y, w, h) in world coords with
  // pixelRatio=1, producing exact W×H output pixels regardless of how
  // the user has the editor zoomed.
  const withResetTransform = (fn) => {
    const stage = stageRef.current;
    if (!stage) return null;
    const prev = { x: stage.x(), y: stage.y(), sx: stage.scaleX(), sy: stage.scaleY() };
    stage.position({ x: 0, y: 0 });
    stage.scale({ x: 1, y: 1 });
    stage.batchDraw();
    try { return fn(); }
    finally {
      stage.position({ x: prev.x, y: prev.y });
      stage.scale({ x: prev.sx, y: prev.sy });
      stage.batchDraw();
    }
  };

  // Imperative export API. Both functions render against the world-coord
  // space and crop to the user-specified canvas rect (or, for selections,
  // the bounding box of the chosen layers).
  useImperativeHandle(
    ref,
    () => ({
      exportStage: () => {
        const stage = stageRef.current;
        if (!stage) return null;
        const cw = canvasSize?.w || 0;
        const ch = canvasSize?.h || 0;
        if (cw <= 0 || ch <= 0) return null;
        return withChromeHidden(() =>
          withResetTransform(() =>
            stage.toDataURL({
              x: 0, y: 0, width: cw, height: ch,
              pixelRatio: 1, mimeType: "image/png",
            })
          )
        );
      },
      exportNodes: (layerIds, { pixelRatio = 2, padding = 8 } = {}) => {
        const stage = stageRef.current;
        if (!stage) return null;
        const ids = layerIds || [];
        if (ids.length === 0) {
          // Fallback: behave like exportStage when no selection given.
          const cw = canvasSize?.w || 0;
          const ch = canvasSize?.h || 0;
          if (cw <= 0 || ch <= 0) return null;
          return withChromeHidden(() =>
            withResetTransform(() =>
              stage.toDataURL({
                x: 0, y: 0, width: cw, height: ch,
                pixelRatio: 1, mimeType: "image/png",
              })
            )
          );
        }
        // Bounding box in world coords (relativeTo: stage with identity
        // transform === world coords, so we reset the transform first).
        return withChromeHidden(() =>
          withResetTransform(() => {
            let bbox = null;
            for (const id of ids) {
              const n = layerNodesRef.current.get(id);
              if (!n) continue;
              const r = n.getClientRect({ relativeTo: stage });
              if (!bbox) bbox = { x: r.x, y: r.y, x2: r.x + r.width, y2: r.y + r.height };
              else {
                bbox.x = Math.min(bbox.x, r.x);
                bbox.y = Math.min(bbox.y, r.y);
                bbox.x2 = Math.max(bbox.x2, r.x + r.width);
                bbox.y2 = Math.max(bbox.y2, r.y + r.height);
              }
            }
            if (!bbox) return null;
            const x = bbox.x - padding;
            const y = bbox.y - padding;
            const width = bbox.x2 - bbox.x + padding * 2;
            const height = bbox.y2 - bbox.y + padding * 2;
            return stage.toDataURL({ x, y, width, height, pixelRatio, mimeType: "image/png" });
          })
        );
      },
    }),
    [canvasSize?.w, canvasSize?.h],
  );

  return (
    <div
      ref={containerRef}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onContextMenu={(e) => e.preventDefault()}
      // image-rendering: pixelated on the inner <canvas> elements stops
      // any CSS-level browser smoothing during DPR mismatches or zoom.
      // Pairs with the Layer's imageSmoothingEnabled={false} so the
      // pixels stay sharp at every step in the rendering pipeline.
      className="relative h-full w-full overflow-hidden bg-white [&_canvas]:[image-rendering:pixelated]"
    >
      <Stage
        ref={stageRef}
        width={size.w}
        height={size.h}
        scaleX={view.scale}
        scaleY={view.scale}
        x={view.x}
        y={view.y}
        onWheel={handleWheel}
        onMouseDown={handleStageClick}
        onTouchStart={handleStageClick}
      >
        <Layer imageSmoothingEnabled={false}>
          <Rect
            ref={frameRef}
            x={0}
            y={0}
            width={canvasSize?.w || 0}
            height={canvasSize?.h || 0}
            stroke="#9CA3AF"
            // Stroke width compensates for zoom so the line looks
            // 1 device pixel thick at any zoom level.
            strokeWidth={1 / Math.max(0.001, view.scale)}
            strokeScaleEnabled={false}
            listening={false}
            perfectDrawEnabled={false}
          />
          {layers.map((layer) => (
            <LayerNode
              key={layer.id}
              layer={layer}
              isSelected={selectedIds.includes(layer.id)}
              selectedIds={selectedIds}
              onSelect={(e) => handleSelectFromCanvas(layer.id, e)}
              onChange={(patch) => onTransformLayer(layer.id, patch)}
              onMultiDragStart={handleMultiDragStart}
              onMultiDragMove={handleMultiDragMove}
              onMultiDragEnd={handleMultiDragEnd}
              onAltDuplicate={handleAltDuplicate}
              onContextMenu={(e) => {
                e.evt?.preventDefault();
                if (!selectedIds.includes(layer.id)) onSelectLayers([layer.id]);
                if (onLayerContextMenu) {
                  onLayerContextMenu({
                    layerId: layer.id,
                    x: e.evt?.clientX ?? 0,
                    y: e.evt?.clientY ?? 0,
                  });
                }
              }}
              registerNode={registerNode}
            />
          ))}
          <Transformer
            ref={transformerRef}
            rotateEnabled={true}
            keepRatio={false}
            anchorSize={9}
            borderStroke="#3b82f6"
            anchorStroke="#3b82f6"
            anchorFill="#1e40af"
            anchorCornerRadius={2}
          />
        </Layer>
      </Stage>
    </div>
  );
});

export default Canvas;
